import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polygon, Tooltip, Polyline, ZoomControl, useMap } from 'react-leaflet';
import { createPortal } from 'react-dom';
import {Typography, Button, IconButton, Tooltip as MuiTooltip, Menu, MenuItem } from "@mui/material"
import { renderToStaticMarkup } from 'react-dom/server';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import LayersIcon from '@mui/icons-material/Layers';
import {  IconDisplay, RoomIcons, IconsList } from '../../../Utils/IconDisplay.js'; 
import L, { CRS } from 'leaflet';
import 'leaflet.pattern';
import  {convertMultiPolygon, calculateCentroid} from '../../../Utils/ConvertGeom.js';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { getMarkIcon, getRouteColor, getPointsAlongPath } from '../../../Utils/RouteUtils';
import config, { environment } from '../../../config.js';

const defaultIcon = new L.Icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',  
    iconSize: [25, 41],  
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
  });

const locationIcon = L.divIcon({
    html: renderToStaticMarkup(<MyLocationIcon sx={{ fontSize: '28px' }} color="primary" />),
    className: 'custom-location-icon',
    iconSize: [28, 28],
    iconAnchor: [14, 14],
});

const MapComponentFunc = ({ dispatch, showTouristRoutes, setShowTouristRoutes, mapType, setMapType }) => {
    const map = useMap();
    const { t } = useTranslation();
    const [fsContainer, setFsContainer] = useState(null);
    const [locContainer, setLocContainer] = useState(null);
    const [touristContainer, setTouristContainer] = useState(null);
    const [layersContainer, setLayersContainer] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [userLocation, setUserLocation] = useState(null);

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleLayersClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMapTypeChange = (type) => {
        setMapType(type);
        handleClose();
    };

    useEffect(() => {
        // Fullscreen control creation
        const FullscreenControl = L.Control.extend({
            onAdd: function() {
                const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
                const button = L.DomUtil.create('a', 'fullscreen-button', container);
                button.href = '#';
                button.title = 'Toggle Fullscreen';
                button.style.backgroundColor = 'white';
                button.style.width = '30px';
                button.style.height = '30px';
                button.style.display = 'flex';
                button.style.alignItems = 'center';
                button.style.justifyContent = 'center';
                button.style.cursor = 'pointer';

                L.DomEvent.disableClickPropagation(button);
                L.DomEvent.on(button, 'click', function(e) {
                    L.DomEvent.stop(e);
                    toggleFullscreen();
                });

                setFsContainer(button);
                return container;
            },
            onRemove: function() {
                setFsContainer(null);
            }
        });

        // Tourist routes control creation
        const TouristControl = L.Control.extend({
            onAdd: function() {
                const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
                const button = L.DomUtil.create('a', 'tourist-button fullscreen-button', container);
                button.href = '#';
                button.title = 'Toggle Tourist Routes';
                button.style.backgroundColor = 'white';
                button.style.width = '30px';
                button.style.height = '30px';
                button.style.display = 'flex';
                button.style.alignItems = 'center';
                button.style.justifyContent = 'center';
                button.style.cursor = 'pointer';

                L.DomEvent.disableClickPropagation(button);
                L.DomEvent.on(button, 'click', function(e) {
                    L.DomEvent.stop(e);
                    setShowTouristRoutes(prev => !prev);
                });

                setTouristContainer(button);
                return container;
            },
            onRemove: function() {
                setTouristContainer(null);
            }
        });

        // Layers control creation
        const LayersControl = L.Control.extend({
            onAdd: function() {
                const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
                const button = L.DomUtil.create('a', 'layers-button fullscreen-button', container);
                button.href = '#';
                button.title = 'Change Map Layer';
                button.style.backgroundColor = 'white';
                button.style.width = '30px';
                button.style.height = '30px';
                button.style.display = 'flex';
                button.style.alignItems = 'center';
                button.style.justifyContent = 'center';
                button.style.cursor = 'pointer';

                L.DomEvent.disableClickPropagation(button);
                L.DomEvent.on(button, 'click', function(e) {
                    L.DomEvent.stop(e);
                    handleLayersClick(e);
                });

                setLayersContainer(button);
                return container;
            },
            onRemove: function() {
                setLayersContainer(null);
            }
        });

        // Location control creation
        const LocationControl = L.Control.extend({
            onAdd: function() {
                const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
                const button = L.DomUtil.create('a', 'location-button fullscreen-button', container);
                button.href = '#';
                button.title = 'Center on My Location';
                button.style.backgroundColor = 'white';
                button.style.width = '30px';
                button.style.height = '30px';
                button.style.display = 'none'; // Hidden by default
                button.style.alignItems = 'center';
                button.style.justifyContent = 'center';
                button.style.cursor = 'pointer';

                L.DomEvent.disableClickPropagation(button);
                L.DomEvent.on(button, 'click', function(e) {
                    L.DomEvent.stop(e);
                    if (userLocation) {
                        map.flyTo(userLocation, map.getZoom());
                    }
                });

                setLocContainer(button);
                return container;
            },
            onRemove: function() {
                setLocContainer(null);
            }
        });

        const fsControl = new FullscreenControl({ position: 'topright' });
        const locControl = new LocationControl({ position: 'topright' });
        const touristControl = new TouristControl({ position: 'topright' });
        const layersControl = new LayersControl({ position: 'topright' });
        map.addControl(fsControl);
        map.addControl(locControl);
        map.addControl(touristControl);
        map.addControl(layersControl);

        // Define patterns using leaflet.pattern
        const nations = [
            { id: 'szekely', color: 'blue' },
            { id: 'magyar', color: 'green' },
            { id: 'avar', color: 'olive' },
            { id: 'besenyo', color: 'gold' },
            { id: 'szasz', color: 'brown' }
        ];

        nations.forEach((nation, index) => {
            const stripe = new L.StripePattern({
                color: nation.color,
                weight: 2,
                spaceWeight: 6,
                angle: 45 + (index * 20),
                opacity: 0.9
            });
            stripe.addTo(map);
            // Store reference on L for easy access in components
            if (!L.Patterns) L.Patterns = {};
            L.Patterns[nation.id] = stripe;
        });

        dispatch({ type: 'getMap', mapData: map });

        return () => {
            map.removeControl(fsControl);
            map.removeControl(locControl);
            map.removeControl(touristControl);
        };
    }, [dispatch, map, userLocation, setShowTouristRoutes]);

    useEffect(() => {
        const handleFullscreenChange = () => {
            const isFs = !!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement);
            setIsFullscreen(isFs);
            
            if (isFs) {
                map.locate({ setView: false, watch: true });
            } else {
                map.stopLocate();
                setUserLocation(null);
            }

            setTimeout(() => {
                map.invalidateSize();
            }, 100);
        };

        const onLocationFound = (e) => {
            setUserLocation(e.latlng);
        };

        map.on('locationfound', onLocationFound);
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);
        
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
            map.off('locationfound', onLocationFound);
            map.stopLocate();
        };
    }, [map]);

    // Fullscreen styling for Leaflet container
    useEffect(() => {
        const mapElement = map.getContainer();
        if (isFullscreen && mapElement) {
            const isActuallyFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement);
            if (!isActuallyFullscreen) {
                mapElement.style.position = 'fixed';
                mapElement.style.top = '0';
                mapElement.style.left = '0';
                mapElement.style.width = '100vw';
                mapElement.style.height = '100vh';
                mapElement.style.zIndex = '1300'; // Ensure it's above everything including header (1200)
                map.invalidateSize();
            } else {
                // If it is actually in fullscreen mode, we reset these styles because the browser handles it
                mapElement.style.position = '';
                mapElement.style.top = '';
                mapElement.style.left = '';
                mapElement.style.width = '100%';
                mapElement.style.height = '100%';
                mapElement.style.zIndex = '';
                map.invalidateSize();
            }
        } else if (mapElement) {
            mapElement.style.position = '';
            mapElement.style.top = '';
            mapElement.style.left = '';
            mapElement.style.width = '';
            mapElement.style.height = '';
            mapElement.style.zIndex = '';
            map.invalidateSize();
        }
    }, [isFullscreen, map]);

    useEffect(() => {
        if (locContainer) {
            locContainer.style.display = (isFullscreen && userLocation) ? 'flex' : 'none';
        }
    }, [isFullscreen, userLocation, locContainer]);

    const toggleFullscreen = () => {
        const mapContainer = map.getContainer().parentElement; // This is the div#map-container
        const isCurrentlyFs = !!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement);

        if (!isCurrentlyFs) {
            if (mapContainer.requestFullscreen) {
                mapContainer.requestFullscreen();
            } else if (mapContainer.webkitRequestFullscreen) { /* Safari / iOS */
                mapContainer.webkitRequestFullscreen();
            } else if (mapContainer.mozRequestFullScreen) {
                mapContainer.mozRequestFullScreen();
            } else if (mapContainer.msRequestFullscreen) { /* IE11 */
                mapContainer.msRequestFullscreen();
            } else {
                // Pseudo-fullscreen fallback for iOS Safari or browsers without API
                setIsFullscreen(true);
                setTimeout(() => map.invalidateSize(), 200);
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) { /* Safari */
                document.webkitExitFullscreen();
            } else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
            } else if (document.msExitFullscreen) { /* IE11 */
                document.msExitFullscreen();
            } else {
                setIsFullscreen(false);
                setTimeout(() => map.invalidateSize(), 200);
            }
        }
    };

    return (
        <>
            {fsContainer && createPortal(
                isFullscreen ? <FullscreenExitIcon sx={{ fontSize: '28px' }} /> : <FullscreenIcon sx={{ fontSize: '28px' }} />,
                fsContainer
            )}
            {locContainer && createPortal(
                <MyLocationIcon sx={{ fontSize: '28px' }} color="primary" />,
                locContainer
            )}
            {touristContainer && createPortal(
                <DirectionsWalkIcon sx={{ fontSize: '28px' }} color={showTouristRoutes ? "error" : "inherit"} />,
                touristContainer
            )}
            {layersContainer && createPortal(
                <LayersIcon sx={{ fontSize: '28px' }} />,
                layersContainer
            )}

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <MenuItem onClick={() => handleMapTypeChange('cyclosm')} selected={mapType === 'cyclosm'}>
                    {t('Common.CyclOSM')}
                </MenuItem>
                <MenuItem onClick={() => handleMapTypeChange('herbich')} selected={mapType === 'herbich'}>
                    {t('Common.HerbichGeo')}
                </MenuItem>
                <MenuItem onClick={() => handleMapTypeChange('nszn')} selected={mapType === 'nszn'}>
                    {t('Common.NsznHonismereti', 'NSZN Honismereti')}
                </MenuItem>
                <MenuItem onClick={() => handleMapTypeChange('geo200000')} selected={mapType === 'geo200000'}>
                    {t('Common.Geo200000', 'Geo 200000')}
                </MenuItem>
            </Menu>

            {userLocation && (
                <Marker position={userLocation} icon={locationIcon}>
                    <Popup>{t('Map.YouAreHere', 'Te itt vagy')}</Popup>
                </Marker>
            )}
        </>
    );
}

const CsangoKozsegVarosComponent = ({ parish, index }) => {
  const map = useMap();
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(map.getZoom());

  useEffect(() => {
    const onZoom = () => setZoom(map.getZoom());
    map.on('zoomend', onZoom);
    return () => map.off('zoomend', onZoom);
  }, [map]);

  if (!parish.geom) return null;
  let positions;
  if (parish.geom.type === 'Polygon') {
    positions = parish.geom.coordinates[0].map(coord => [coord[1], coord[0]]);
  } else if (parish.geom.type === 'MultiPolygon') {
    positions = convertMultiPolygon(parish.geom);
  } else {
    return null;
  }
  const zoomLevelToShowTooltip = 11;
  return (
    <Polygon
      key={index}
      positions={positions}
      color="#9c27b0" 
      fillColor="#e1bee7"
      fillOpacity={0.1} 
      weight={1}
    >
      <Popup>
        <span style={{ fontWeight: 'bold' }}>{parish.nev}</span><br />
        <span style={{ fontSize: '0.8em', fontStyle: 'italic' }}>{parish.name}</span>
      </Popup>
          {zoom > zoomLevelToShowTooltip && (
        <Tooltip permanent direction="center" opacity={1} className="polygon-label" offset={[0, 0]}>
          <span className="polygon-hu-label" style={{ color: '#d32f2f' }}>{parish.nev}</span><br />
          <span className="polygon-ro-label">{parish.name}</span>
        </Tooltip>
      )}
    </Polygon>
  );
};

const CsangoTelepulesComponent = ({ telepules, index }) => {
  const map = useMap();
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(map.getZoom());

  useEffect(() => {
    const onZoom = () => setZoom(map.getZoom());
    map.on('zoomend', onZoom);
    return () => map.off('zoomend', onZoom);
  }, [map]);

  if (!telepules.geom) return null;

  if (telepules.geom.type === 'Point') {
    const position = [telepules.geom.coordinates[1], telepules.geom.coordinates[0]];
    const zoomLevelToShowTooltip = 12;

    return (
      <Marker key={index} position={position} icon={IconsList.csangoTelepulesIcon || defaultIcon}>
        <Popup>
          <span style={{ fontWeight: 'bold' }}>{telepules.nev_hu}</span><br />
          <span style={{ fontSize: '0.8em', fontStyle: 'italic' }}>{telepules.nev_ro}</span>
        </Popup>
        {zoom > zoomLevelToShowTooltip && (
          <Tooltip permanent direction="right" opacity={0.7} offset={[10, 0]}>
            <span>{telepules.nev_hu}</span><br />
            <span style={{ fontSize: '0.8em', fontStyle: 'italic' }}>{telepules.nev_ro}</span>
          </Tooltip>
        )}
      </Marker>
    );
  } else if (telepules.geom.type === 'Polygon' || telepules.geom.type === 'MultiPolygon') {
    let positions;
    if (telepules.geom.type === 'Polygon') {
      positions = telepules.geom.coordinates[0].map(coord => [coord[1], coord[0]]);
    } else {
      positions = convertMultiPolygon(telepules.geom);
    }
    const zoomLevelToShowTooltip = 12;

    return (
      <Polygon
        key={index}
        positions={positions}
        color="#4B0082" 
        fillColor="#6A5ACD"
        fillOpacity={0.3}
        weight={1}
      >
        <Popup>
          <span style={{ fontWeight: 'bold' }}>{telepules.nev_hu}</span><br />
          <span style={{ fontSize: '0.8em', fontStyle: 'italic' }}>{telepules.nev_ro}</span>
        </Popup>
          {zoom > zoomLevelToShowTooltip && (
          <Tooltip permanent direction="center" opacity={1} className="polygon-label" offset={[0, 0]}>
            <span className="polygon-hu-label" style={{ color: '#d32f2f' }}>{telepules.nev_hu}</span><br />
            <span className="polygon-ro-label">{telepules.nev_ro}</span>
          </Tooltip>
        )}
      </Polygon>
    );
  }

  return null;
};

const KozsegVarosComponent = ({parish, index}) => {
  const map = useMap();
  const { t } = useTranslation();
  let positions;
  let centroid;
  if (parish.geom.type === 'Polygon') {
      positions = parish.geom.coordinates[0].map(coord => [coord[1], coord[0]]); // swap lat and lon
      centroid = calculateCentroid(positions); // calculate centroid
  } else if (parish.geom.type === 'MultiPolygon') {
      // For MultiPolygon geometry, use the convertMultiPolygon function
      positions = convertMultiPolygon(parish.geom);
      centroid = calculateCentroid(positions); // calculate centroid
  } else {
      console.error(`Unsupported geometry type: ${parish.geom.type}`);
      return null;
  }
  const zoomLevelToShowTooltip = 11;
  return (
      <Polygon
          key={index}
          positions={positions}
          color="#8E44AD"
          fillColor="#D2B4DE"
          fillOpacity={0.1}
          weight={1}
      >
          {map.getZoom() > zoomLevelToShowTooltip && (
            <Tooltip permanent direction="center" opacity={1} className="polygon-label" offset={[0, 0]}>
              <span className="polygon-sic-label">{parish.nevsic}</span><br />
              <span className="polygon-hu-label" style={{ color: '#d32f2f' }}>{parish.nev}</span><br />
              <span className="polygon-ro-label">{parish.name}</span>
            </Tooltip>
          )}
      </Polygon>
  );
}

const TelepulesComponent = ({telepules, index}) => {
  const map = useMap();
  const { t } = useTranslation();
  let positions;
  let centroid;
  if (telepules.geom.type === 'Polygon') {
      positions = telepules.geom.coordinates[0].map(coord => [coord[1], coord[0]]); // swap lat and lon
      centroid = calculateCentroid(positions); // calculate centroid
  } else if (telepules.geom.type === 'MultiPolygon') {
      positions = convertMultiPolygon(telepules.geom);
      centroid = calculateCentroid(positions); // calculate centroid
  } else {
      console.error(`Unsupported geometry type: ${telepules.geom.type}`);
      return null;
  }
  const zoomLevelToShowTooltip = 11;
  return (
    <Polygon
        key={index}
        positions={positions}
        color="#9B59B6"
        fillColor="#EBDEF0"
        fillOpacity={0.2}
        weight={1}
    >
        <Popup>
            <Typography variant="h6">{telepules.nev}</Typography>
            {telepules.nevro && telepules.nevro !== telepules.nev && (
                <Typography variant="subtitle2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                    {telepules.nevro}
                </Typography>
            )}
            {telepules.alapitva && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    {t('Map.Founded', 'Alapítva')}: {telepules.alapitva}
                </Typography>
            )}
        </Popup>
        {map.getZoom() > zoomLevelToShowTooltip && (
            <Tooltip permanent direction="center" opacity={1} className="polygon-label" offset={[0, 0]}>
              <span className="polygon-hu-label" style={{ color: '#d32f2f' }}>{telepules.nev}</span><br />
              <span className="polygon-ro-label">{telepules.nevro}</span>
            </Tooltip>
          )}
    </Polygon>
  );
}

const SzekComponent = ({szek, index}) => {
  const map = useMap();
  const { t } = useTranslation();
  let positions;
  let centroid;
  if (szek.geom.type === 'Polygon') {
      positions = szek.geom.coordinates[0].map(coord => [coord[1], coord[0]]); // swap lat and lon
      centroid = calculateCentroid(positions); // calculate centroid
  } else if (szek.geom.type === 'MultiPolygon') {
      positions = convertMultiPolygon(szek.geom);
      centroid = calculateCentroid(positions); // calculate centroid
  } else {
      console.error(`Unsupported geometry type: ${szek.geom.type}`);
      return null;
  }
  const zoomLevelToShowTooltip = 8;
  return (
      <Polygon
          key={index}
          positions={positions}
          color="#FFD700"
          fillColor="#2492be"
          fillOpacity={0}
          weight={3}
      > 
          {map.getZoom() > zoomLevelToShowTooltip && (
              <Tooltip permanent direction="center" opacity={0.8} className="szek-label" offset={[0, 0]}>
                  <span className="szek-hu-label">{szek.nev}</span><br />
                  <span className="szek-lat-label">{szek.name}</span>
              </Tooltip>
          )}
      </Polygon>
  );
}

const MegyeComponent = ({megye, index}) => {
  const map = useMap();
  const { t } = useTranslation();
  let positions;
  let centroid;
  if (megye.geom.type === 'Polygon') {
      positions = megye.geom.coordinates[0].map(coord => [coord[1], coord[0]]); // swap lat and lon
      centroid = calculateCentroid(positions); // calculate centroid
  } else if (megye.geom.type === 'MultiPolygon') {
      positions = convertMultiPolygon(megye.geom);
      centroid = calculateCentroid(positions); // calculate centroid
  } else {
      console.error(`Unsupported geometry type: ${megye.geom.type}`);
      return null;
  }
  const zoomLevelToShowTooltip = 8;
  return (
      <Polygon
          key={index}
          positions={positions}
          color="#012837" 
          fillOpacity={0}
          weight={3} 
          pathOptions={{dashArray: '5, 10'}}        
      >
          {map.getZoom() > zoomLevelToShowTooltip && (
              <Tooltip permanent direction="center" opacity={0.8} className="megye-label" offset={[0, 0]}>
                  
                  <span className="megye-ro-label">{megye.name}</span>
              </Tooltip>
          )}
      </Polygon>
  );
}

const HelynevComponent = ({helynev}) => {
  const map = useMap();
  const { t } = useTranslation();
  let position;
  if (helynev.geom.type === 'Point') {
      position = [helynev.geom.coordinates[1], helynev.geom.coordinates[0]]; // swap lat and lon
  } else {
      console.error(`Unsupported geometry type: ${helynev.geom.type}`);
      return null;
  }
  const icon = IconDisplay('csucs');
  return (
      <Marker
          key={helynev.id}
          position={position} 
          icon={icon ? icon : defaultIcon }
          opacity={1}       
      >
          <Tooltip  direction="top" opacity={0.7} className="helynev-label" offset={[0, 0]}>
              <span className="helynev-hu-label">{helynev.nev}</span>
          </Tooltip>
      </Marker>
  );
}

const FolyovizComponent = ({ folyoviz, index }) => {
    const map = useMap();
    const { t } = useTranslation();
    let positions;
    if (folyoviz.geom.type === 'MultiLineString') {
        positions = folyoviz.geom.coordinates.map(line => line.map(coord => [coord[1], coord[0]]));
    } else if (folyoviz.geom.type === 'LineString') {
        positions = folyoviz.geom.coordinates.map(coord => [coord[1], coord[0]]);
    } else {
        console.error(`Unsupported geometry type for Folyoviz: ${folyoviz.geom.type}`);
        return null;
    }

    const zoomLevelToShowTooltip = 11;

    return (
        <Polyline
            key={index}
            positions={positions}
            color="#3388ff"
            weight={3}
            opacity={0.7}
        >
            {map.getZoom() > zoomLevelToShowTooltip && (
                <Tooltip permanent direction="center" opacity={0.9} className="folyoviz-label" offset={[0, 0]}>
                    <span className="folyoviz-name">{folyoviz.folyoviz_h}</span>
                </Tooltip>
            )}
        </Polyline>
    );
};

const GenericGISComponent = ({ item, category, navigate, navigatePath }) => {
    const { t } = useTranslation();
    const map = useMap();
    const icon = IconDisplay(category);
    let position;
    let positions;
    let type = 'Marker';

    if (item.geom.type === 'Point') {
        position = [item.geom.coordinates[1], item.geom.coordinates[0]];
    } else if (item.geom.type === 'MultiPolygon' || item.geom.type === 'Polygon') {
        if (item.geom.type === 'Polygon') {
            positions = item.geom.coordinates[0].map(coord => [coord[1], coord[0]]);
        } else {
            positions = convertMultiPolygon(item.geom);
        }
        position = calculateCentroid(positions);
        type = 'Polygon';
    } else if (item.geom.type === 'MultiLineString' || item.geom.type === 'LineString') {
        if (item.geom.type === 'LineString') {
            positions = item.geom.coordinates.map(coord => [coord[1], coord[0]]);
        } else {
            positions = item.geom.coordinates.map(line => line.map(coord => [coord[1], coord[0]]));
        }
        type = 'Polyline';
    }

    const getNationStyle = (nemzet) => {
        const n = nemzet ? nemzet.toLowerCase() : '';
        if (n === 'székely') return { color: 'blue', hatch: 'szekely' };
        if (n === 'magyar') return { color: 'green', hatch: 'magyar' };
        if (n === 'avar') return { color: 'olive', hatch: 'avar' };
        if (n === 'besenyő') return { color: 'gold', hatch: 'besenyo' };
        if (n === 'szász') return { color: 'brown', hatch: 'szasz' };
        return { color: 'blue', hatch: '' };
    };

    const nationStyle = category === 'Letelepedes' ? getNationStyle(item.nemzet) : { color: 'blue', hatch: '' };
    
    // Custom color for Sipalya (Ski tracks)
    const polylineColor = category === 'Sipalya' ? 'red' : 'brown';
    const polylineWeight = category === 'Sipalya' ? 5 : 4;

    return (
        <>
            {type === 'Marker' && (
                <Marker position={position} icon={icon ? icon : defaultIcon}>
                    <Popup>
                        <Typography variant="h6">{item.nev || item.title || item.name}</Typography>
                        {category === 'Letelepedes' && item.nemzet && (
                            <Typography variant="body2" color="textSecondary">{item.nemzet}</Typography>
                        )}
                        {category === 'Rovasemlek' ? (
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>{item.hely}</Typography>
                        ) : (
                            (item.name || item.name_ro) && (item.nev || item.title) && (item.name || item.name_ro) !== (item.nev || item.title) && (
                                <Typography variant="subtitle2" color="textSecondary">{item.name || item.name_ro}</Typography>
                            )
                        )}
                        {item.picture1 && (
                            <div style={{ maxHeight: "14rem", maxWidth: "20rem", overflow: "hidden" }}>
                                <img
                                    src={item.picture1}
                                    alt={item.nev}
                                    style={{ width: "100%", height: "auto", cursor: 'pointer' }}
                                    onClick={() => navigatePath && navigate(navigatePath)}
                                />
                            </div>
                        )}
                        {navigatePath && <Button variant="contained" fullWidth onClick={() => navigate(navigatePath)}>{t('Map.Details')}</Button>}
                    </Popup>
                </Marker>
            )}
            {type === 'Polygon' && (
                <Polygon 
                    positions={positions} 
                    color={nationStyle.color} 
                    fillOpacity={0.4}
                    pathOptions={{
                        fillPattern: nationStyle.hatch && L.Patterns ? L.Patterns[nationStyle.hatch] : null,
                        fillColor: nationStyle.color
                    }}
                >
                     {category !== 'Letelepedes' && (
                         <Popup>
                            <Typography variant="h6">{item.nev || item.title || item.name}</Typography>
                            {category === 'Rovasemlek' ? (
                                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>{item.hely}</Typography>
                            ) : (
                                (item.name || item.name_ro) && (item.nev || item.title) && (item.name || item.name_ro) !== (item.nev || item.title) && (
                                    <Typography variant="subtitle2" color="textSecondary">{item.name || item.name_ro}</Typography>
                                )
                            )}
                         </Popup>
                     )}
                     {category === 'Letelepedes' && (
                        <Tooltip permanent direction="center" className="letelepedes-polygon-label">
                            {item.nev || t('Map.Settlement')}
                        </Tooltip>
                     )}
                     {category === 'Letelepedes' && (
                         <Popup>
                            <Typography variant="h6">{item.nev || t('Map.Settlement')}</Typography>
                            {item.nemzet && (
                                <Typography variant="subtitle2" color="textSecondary">{item.nemzet}</Typography>
                            )}
                         </Popup>
                     )}
                </Polygon>
            )}
            {type === 'Polyline' && (
                <Polyline positions={positions} color={polylineColor} weight={polylineWeight}>
                    <Popup>
                        <Typography variant="h6">{item.nev || item.title || item.name}</Typography>
                        {category === 'Rovasemlek' ? (
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>{item.hely}</Typography>
                        ) : (
                            (item.name || item.name_ro) && (item.nev || item.title) && (item.name || item.name_ro) !== (item.nev || item.title) && (
                                <Typography variant="subtitle2" color="textSecondary">{item.name || item.name_ro}</Typography>
                            )
                        )}
                    </Popup>
                    {category === 'Sipalya' && (item.nev || item.title || item.name) && (
                        <Tooltip direction="top" opacity={0.9} sticky>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                {item.nev || item.title || item.name}
                            </Typography>
                        </Tooltip>
                    )}
                </Polyline>
            )}
        </>
    );
};

const TouristRoutesLayer = ({ allTouristRoutes, showTouristRoutes }) => {
    const map = useMap();
    const [zoom, setZoom] = useState(map.getZoom());
    const isMobile = window.innerWidth < 900;

    useEffect(() => {
        const onZoom = () => setZoom(map.getZoom());
        map.on('zoomend', onZoom);
        return () => map.off('zoomend', onZoom);
    }, [map]);

    if (!showTouristRoutes) return null;

    return (
        <>
            {allTouristRoutes.tourist_routes?.features?.map((feature) => {
                const positions = feature.geometry.type === 'MultiLineString' 
                    ? feature.geometry.coordinates.map(line => line.map(c => [c[1], c[0]]))
                    : feature.geometry.coordinates.map(c => [c[1], c[0]]);

                return (
                    <Polyline
                        key={`route-${feature.id}`}
                        positions={positions}
                        color={getRouteColor(feature.properties.jelzes || feature.properties.szimbolum)}
                        weight={4}
                        opacity={0.8}
                    >
                        <Popup>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{feature.properties.nev}</Typography>
                            {feature.properties.leiras && <Typography variant="body2">{feature.properties.leiras}</Typography>}
                        </Popup>
                    </Polyline>
                );
            })}
            {allTouristRoutes.marked_paths?.features?.map((feature) => {
                const markIcon = getMarkIcon(feature.properties.szimbolum, zoom, isMobile);
                const positions = feature.geometry.type === 'MultiLineString' 
                    ? feature.geometry.coordinates.map(line => line.map(c => [c[1], c[0]]))
                    : feature.geometry.coordinates.map(c => [c[1], c[0]]);
                
                const iconPoints = getPointsAlongPath(positions, zoom, feature.properties.szimbolum);

                return (
                    <React.Fragment key={`marked-${feature.id}`}>
                        <Polyline
                            positions={positions}
                            color={getRouteColor(feature.properties.szimbolum)}
                            weight={3}
                            dashArray="10, 10"
                            opacity={0.7}
                        >
                            <Popup>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{feature.properties.nev}</Typography>
                                <Typography variant="body2">Jelzés: {feature.properties.szimbolum}</Typography>
                            </Popup>
                        </Polyline>
                        {markIcon && iconPoints.map((point, idx) => (
                            <Marker 
                                key={`${feature.id}-icon-${idx}`} 
                                position={point} 
                                icon={markIcon}
                            >
                                <Popup>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{feature.properties.nev}</Typography>
                                    <Typography variant="body2">Jelzés: {feature.properties.szimbolum}</Typography>
                                </Popup>
                            </Marker>
                        ))}
                    </React.Fragment>
                );
            })}
        </>
    );
};

const MapComponent = React.memo(({ allListings, allBuiltHeritage, allAccommodation, allMarketplaceAccommodation = [], allParishes, allTelepulesek, allCsangoParishes = [], allCsangoTelepulesek = [],
  showCsango = false,
  allSzekek, allMegyek, allHelynevek, allFolyovizek = [], allHistory = {}, allMineralWater = {}, allSport = {}, allSportEquipment = [], allOwnProduce = [], 
  allTouristRoutes = { tourist_routes: [], marked_paths: [] }, showTouristRoutes, setShowTouristRoutes,
  selectedAdmLayers = [], navigate, dispatch }) => {
    const { t } = useTranslation();
    const [mapType, setMapType] = useState('cyclosm');

    const getTileLayer = () => {
        if (mapType === 'herbich') {
            return {
                url: config[environment].herbichGeoTiles,
                attribution: '&copy; Herbich Geo',
                maxZoom: 18,
                minZoom: 6,
                tms: false,
                opacity: 1,
                noWrap: false,
                bounds: [[44.0, 20.0], [49.0, 30.0]] // Kárpát-medence környéke kb.
            };
        }
        if (mapType === 'nszn') {
            return {
                url: config[environment].nsznHonismeretiTiles,
                attribution: '&copy; NSZN Honismereti',
                maxZoom: 18,
                minZoom: 6,
                tms: false,
                opacity: 1,
                noWrap: false,
                bounds: [[44.0, 20.0], [49.0, 30.0]]
            };
        }
        if (mapType === 'geo200000') {
            return {
                url: config[environment].geo200000Tiles,
                attribution: '&copy; Geo 200000',
                maxZoom: 18,
                minZoom: 6,
                tms: false,
                opacity: 1,
                noWrap: false,
                bounds: [[44.0, 20.0], [49.0, 30.0]]
            };
        }
        return {
            url: config[environment].cyclOSMTiles,
            attribution: 'Map style: <a href="https://github.com/cyclosm/cyclosm-cartocss-style/releases" title="CyclOSM - Open Bicycle render">CyclOSM</a> | Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            maxZoom: 18,
            minZoom: 1
        };
    };

    const tileLayerProps = getTileLayer();

    return (
    <MapContainer center={[45.8643, 25.7848]} zoom={14} scrollWheelZoom={true} className='leaflet-container' zoomControl={false} style={{ height: '100%', width: '100%' }}>
    <ZoomControl position="topleft" />
    <TileLayer                    
      key={mapType}
      attribution = {tileLayerProps.attribution}
      url = {tileLayerProps.url}
      maxZoom={tileLayerProps.maxZoom}
      minZoom={tileLayerProps.minZoom}
      tms={tileLayerProps.tms}
      opacity={tileLayerProps.opacity}
      noWrap={tileLayerProps.noWrap}
      bounds={tileLayerProps.bounds}
    />
    <MapComponentFunc dispatch={dispatch} showTouristRoutes={showTouristRoutes} setShowTouristRoutes={setShowTouristRoutes} mapType={mapType} setMapType={setMapType}/>
    
    <TouristRoutesLayer allTouristRoutes={allTouristRoutes} showTouristRoutes={showTouristRoutes} />

    {allListings.map((listing) => {
        const icon = IconDisplay(listing.listing_type);
        const getDetailsPath = (listing) => {
            const lang = i18n.language || 'hu';
            if (listing.key === 'Auto' || listing.listing_type === 'auto' || listing.listing_type === 'Auto') {
                return `/${lang}/car/${listing.id}`;
            }
            if (listing.key === 'Property' || listing.listing_type === 'ingatlan' || listing.listing_type === 'Ingatlan') {
                return `/${lang}/property/${listing.id}`;
            }
            if (listing.key === 'Telisportfelszereles') {
                return `/${lang}/winter-sport-equipment/${listing.id}`;
            }
            if (listing.key === 'Nyarisportfelszereles') {
                return `/${lang}/summer-sport-equipment/${listing.id}`;
            }
            if (listing.key === 'Sajattermek') {
                return `/${lang}/own-produce/${listing.id}`;
            }
            return `/terkep/${listing.id}`;
        };
        const detailsPath = getDetailsPath(listing);
        return(
            <Marker 
                key={listing.id}
                icon ={icon ? icon : defaultIcon }
                position={[listing.latitude, listing.longitude ]}>
                <Popup>
                    <Typography variant="h6">{listing.title || listing.nev || listing.name}</Typography>
                    {(listing.name || listing.name_ro) && (listing.title || listing.nev) && (listing.name || listing.name_ro) !== (listing.title || listing.nev) && (
                        <Typography variant="subtitle2" color="textSecondary">{listing.name || listing.name_ro}</Typography>
                    )}
                    <div style={{ maxHeight: "14rem", maxWidth: "20rem", overflow: "hidden" }}>
                        {listing.picture1 && (
                            <img 
                                src={listing.picture1} 
                                alt={listing.title} 
                                style={{ width: "100%", height: "auto", cursor: 'pointer' }}
                                onClick={() => navigate(detailsPath)} 
                            />
                        )}
                    </div>
                    {listing.description && <Typography variant="body1">{listing.description.substring(0, 150)} ...</Typography>}
                    <Button variant="contained" fullWidth onClick={() => navigate(detailsPath)}>{t('Map.Details')}</Button>
                </Popup>                       
            </Marker>
        )
    })} 

    {allMarketplaceAccommodation.map((acc) => {
        const icon = IconDisplay(acc.accommodation_type);
        const lang = i18n.language || 'hu';
        const detailsPath = `/${lang}/marketplace-accommodation/${acc.id}`;
        return(
            <Marker 
                key={`market-acc-${acc.id}`}
                icon ={icon ? icon : defaultIcon }
                position={[acc.latitude, acc.longitude ]}>
                <Popup>
                    <Typography variant="h6">{acc.title}</Typography>
                    <div style={{ maxHeight: "14rem", maxWidth: "20rem", overflow: "hidden" }}>
                        {acc.picture1 && (
                            <img 
                                src={acc.picture1} 
                                alt={acc.title} 
                                style={{ width: "100%", height: "auto", cursor: 'pointer' }}
                                onClick={() => navigate(detailsPath)} 
                            />
                        )}
                    </div>
                    {acc.description && <Typography variant="body2" sx={{ mt: 1 }}>{acc.description.substring(0, 100)} ...</Typography>}
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'green', mt: 1 }}>{acc.price_per_night} RON</Typography>
                    <Button variant="contained" fullWidth sx={{ mt: 1 }} onClick={() => navigate(detailsPath)}>{t('Map.Details')}</Button>
                </Popup>                       
            </Marker>
        )
    })}

    {Object.entries(allBuiltHeritage).map(([category, items]) => (
      items.map((bh) => {
        const icon = IconDisplay(category);
        const lang = i18n.language || 'hu';
        const detailsPath = `/${lang}/built_heritage/${bh.id}`;
        let position;
        
        if (bh.geom.type === 'Point') {                    
          position = [bh.geom.coordinates[1], bh.geom.coordinates[0]];
        } else if (bh.geom.type === 'MultiPolygon') {
          // For MultiPolygon geometry, find the centroid
          const coordinates = bh.geom.coordinates[0][0]; // Extract coordinates of the first polygon
          const sum = coordinates.reduce((acc, coord) => [acc[0] + coord[0], acc[1] + coord[1]], [0, 0]);
          const centroid = [sum[0] / coordinates.length, sum[1] / coordinates.length];
          position = centroid;          
        } else {
          console.error(`Unsupported geometry type: ${bh.geom.type}`);
          return null;
        }
        return (
          <Marker
            key={bh.id}
            icon={icon ? icon : defaultIcon}
            position={position}
          >
            <Popup>
              <Typography variant="h6">{bh.nev || bh.name}</Typography>
              {bh.name && bh.nev && bh.name !== bh.nev && (
                <Typography variant="subtitle2" color="textSecondary">{bh.name}</Typography>
              )}
              <div style={{ maxHeight: "14rem", maxWidth: "20rem", overflow: "hidden" }}>
                <img
                  src={bh.picture1}
                  alt={bh.nev}
                  style={{ width: "100%", height: "auto", cursor: 'pointer' }}
                  onClick={() => navigate(detailsPath)}
                />
              </div>
              <Button variant="contained" fullWidth onClick={() => navigate(detailsPath)}>{t('Map.Details')}</Button>
            </Popup>
          </Marker>
        );
      })
    ))}  

    {Object.entries(allAccommodation).map(([category, items]) => (
      items.map((acc) => {
        const icon = IconDisplay(category);
        const lang = i18n.language || 'hu';
        const detailsPath = `/${lang}/accommodation/${acc.id}`;
        let geometries = [];
        if (acc.geom.type === 'Point') {
          geometries.push(
            <Marker
              key={acc.id}
              icon={icon ? icon : defaultIcon}
              position={[acc.geom.coordinates[1], acc.geom.coordinates[0]]}
            >
              <Popup>
                <Typography variant="h6">{acc.nev || acc.name}</Typography>
                {acc.name && acc.nev && acc.name !== acc.nev && (
                  <Typography variant="subtitle2" color="textSecondary">{acc.name}</Typography>
                )}
                <div style={{ maxHeight: "14rem", maxWidth: "20rem", overflow: "hidden" }}>
                  <img
                    src={acc.picture1}
                    alt={acc.nev}
                    style={{ width: "100%", height: "auto", cursor: 'pointer' }}
                    onClick={() => navigate(detailsPath)}
                  />
                </div>
                <Button variant="contained" fullWidth onClick={() => navigate(detailsPath)}>{t('Map.Details')}</Button>
              </Popup>
            </Marker>
          );
        } else if (acc.geom.type === 'MultiPolygon') {
          acc.geom.coordinates.forEach((polygon, index) => {
            // Calculate centroid of polygon
            let centroidX = 0;
            let centroidY = 0;
            for (let i = 0; i < polygon[0].length; i++) {
              centroidX += polygon[0][i][0];
              centroidY += polygon[0][i][1];
            }
            centroidX /= polygon[0].length;
            centroidY /= polygon[0].length;

            // Create marker for centroid
            geometries.push(
              <Marker
                key={`${acc.id}-${index}`}
                position={[centroidY, centroidX]}
                icon={icon ? icon : defaultIcon}
              >
                <Popup>
                  <Typography variant="h6">{acc.nev || acc.name}</Typography>
                  {acc.name && acc.nev && acc.name !== acc.nev && (
                    <Typography variant="subtitle2" color="textSecondary">{acc.name}</Typography>
                  )}
                  <div style={{ maxHeight: "14rem", maxWidth: "20rem", overflow: "hidden" }}>
                    <img
                      src={acc.picture1}
                      alt={acc.nev}
                      style={{ width: "100%", height: "auto", cursor: 'pointer' }}
                      onClick={() => navigate(detailsPath)}
                    />
                  </div>
                  <Button variant="contained" fullWidth onClick={() => navigate(detailsPath)}>{t('Map.Details')}</Button>
                </Popup>
              </Marker>
            );

            // Create polygon
            geometries.push(
              <Polygon
                key={`${acc.id}-polygon-${index}`}
                positions={polygon[0].map(coords => [coords[1], coords[0]])}
              />
            );
          });
        } else {
          console.error(`Unsupported geometry type: ${acc.geom.type}`);
        }
        return geometries;
      }).flat() 
    ))}

    


{selectedAdmLayers.includes('kozsegvaros') && allParishes.map((parish, index) => (
    <KozsegVarosComponent key={`parish-${index}`} parish={parish} index={index} />
))}
{selectedAdmLayers.includes('kozsegvaros') && showCsango && allCsangoParishes.map((parish, index) => (
    <CsangoKozsegVarosComponent key={`csango-parish-${index}`} parish={parish} index={index} />
))}
{selectedAdmLayers.includes('telepules') && allTelepulesek.map((telepules, index) => (
    <TelepulesComponent key={`telepules-${index}`} telepules={telepules} index={index} />
))}
{selectedAdmLayers.includes('telepules') && showCsango && allCsangoTelepulesek.map((telepules, index) => (
    <CsangoTelepulesComponent key={`csango-telepules-${index}`} telepules={telepules} index={index} />
))}
{selectedAdmLayers.includes('szek') && allSzekek.map((szek, index) => (
    <SzekComponent key={`szek-${index}`} szek={szek} index={index} />
))}
{selectedAdmLayers.includes('megye') && allMegyek.map((megye, index) => (
    <MegyeComponent key={`megye-${index}`} megye={megye} index={index} />
))}
{selectedAdmLayers.includes('helynev') && allHelynevek.map((helynev, index) => (
    <HelynevComponent key={`helynev-${index}`} helynev={helynev} index={index} />
))}
{selectedAdmLayers.includes('folyovizek') && allFolyovizek.map((folyoviz, index) => (
    <FolyovizComponent folyoviz={folyoviz} index={index} key={`folyoviz-${index}`} />
))}

    {Object.entries(allHistory).map(([category, items]) => (
        items.map((item, index) => (
            <GenericGISComponent key={`history-${category}-${index}`} item={item} category={category} navigate={navigate} />
        ))
    ))}

    {/* Render Built Heritage sub-layers mapped from other categories */}
    {Object.entries(allBuiltHeritage).map(([category, items]) => {
        if (['kapolna', 'templom', 'var', 'kastely', 'udvarhaz'].includes(category)) {
             return null; 
        }
        return null;
    })}

    {Object.entries(allMineralWater).map(([category, items]) => (
    items.map((item, index) => (
        <GenericGISComponent 
            key={`mineral-${category}-${index}`} 
            item={item} 
            category={category} 
            navigate={navigate} 
            navigatePath={`/mineral_water/${category}/${item.id}`}
        />
    ))
))}

{Object.entries(allSport).map(([category, items]) => (
    items.map((item, index) => {
        return <GenericGISComponent key={`sport-${category}-${index}`} item={item} category={category} navigate={navigate} />;
    })
))}
   
                  
  </MapContainer>
    );
});


export default MapComponent;